import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '@/lib/operator-api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sparkles,
  Check,
  X,
  Pencil,
  Loader2,
  MessageSquare,
  Calendar,
  CreditCard,
  HandMetal,
  ThumbsUp,
  RotateCcw,
  ShieldAlert,
  TrendingUp,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

interface CopilotSuggestion {
  id: string;
  conversationId: string;
  messageId: string | null;
  intent: SuggestionIntent;
  text: string;
  confidence: number;
  status: SuggestionStatus;
  createdAt: string;
  expiresAt: string | null;
}

type SuggestionStatus = 'PENDING' | 'ACCEPTED' | 'DISMISSED' | 'EXPIRED';

type SuggestionIntent =
  | 'GREETING'
  | 'BOOKING_CONFIRM'
  | 'BOOKING_RESCHEDULE'
  | 'PAYMENT_REMINDER'
  | 'GENERAL_REPLY'
  | 'FOLLOW_UP'
  | 'COMPLAINT_RESOLUTION'
  | 'UPSELL';

interface CopilotSuggestionPanelProps {
  conversationId: string;
  onAccept: (text: string) => void;
}

// ============================================================================
// Constants
// ============================================================================

const INTENT_ICONS: Record<SuggestionIntent, typeof MessageSquare> = {
  GREETING: HandMetal,
  BOOKING_CONFIRM: Check,
  BOOKING_RESCHEDULE: Calendar,
  PAYMENT_REMINDER: CreditCard,
  GENERAL_REPLY: MessageSquare,
  FOLLOW_UP: RotateCcw,
  COMPLAINT_RESOLUTION: ShieldAlert,
  UPSELL: TrendingUp,
};

const INTENT_COLORS: Record<SuggestionIntent, string> = {
  GREETING: 'bg-blue-100 text-blue-700 border-blue-200',
  BOOKING_CONFIRM: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  BOOKING_RESCHEDULE: 'bg-amber-100 text-amber-700 border-amber-200',
  PAYMENT_REMINDER: 'bg-purple-100 text-purple-700 border-purple-200',
  GENERAL_REPLY: 'bg-gray-100 text-gray-700 border-gray-200',
  FOLLOW_UP: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  COMPLAINT_RESOLUTION: 'bg-red-100 text-red-700 border-red-200',
  UPSELL: 'bg-orange-100 text-orange-700 border-orange-200',
};

function formatIntent(intent: SuggestionIntent): string {
  return intent
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

// ============================================================================
// Component
// ============================================================================

export default function CopilotSuggestionPanel({
  conversationId,
  onAccept,
}: CopilotSuggestionPanelProps) {
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState<CopilotSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editText, setEditText] = useState('');
  const [editingSuggestionId, setEditingSuggestionId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);

  // Fetch existing suggestions
  const fetchSuggestions = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(
        `/api/operator/copilot/suggestions/${encodeURIComponent(conversationId)}`,
        { signal }
      );
      if (signal?.aborted) return;
      if (!res.ok) {
        throw new Error(`Failed to load suggestions: ${res.status}`);
      }
      const data = await res.json();
      const list: CopilotSuggestion[] = data.suggestions || data.data || [];
      setSuggestions(list.filter((s) => s.status === 'PENDING'));
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load suggestions');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [conversationId]);

  // SSE connection for real-time suggestion updates
  useEffect(() => {
    const controller = new AbortController();
    fetchSuggestions(controller.signal);

    const API_URL = import.meta.env.VITE_API_URL || '';
    const token = localStorage.getItem('auth_token');
    const sseUrl = `${API_URL}/api/operator/copilot/suggestions/stream/${encodeURIComponent(conversationId)}${token ? `?token=${encodeURIComponent(token)}` : ''}`;

    try {
      const es = new EventSource(sseUrl);
      eventSourceRef.current = es;

      es.onmessage = (event) => {
        if (controller.signal.aborted) return;
        try {
          const payload = JSON.parse(event.data);
          if (payload.type === 'suggestion' && payload.suggestion) {
            const newSuggestion: CopilotSuggestion = payload.suggestion;
            setSuggestions((prev) => {
              const exists = prev.some((s) => s.id === newSuggestion.id);
              if (exists) {
                return prev.map((s) =>
                  s.id === newSuggestion.id ? newSuggestion : s
                );
              }
              return [...prev, newSuggestion].filter((s) => s.status === 'PENDING');
            });
          }
          if (payload.type === 'remove' && payload.suggestionId) {
            setSuggestions((prev) =>
              prev.filter((s) => s.id !== payload.suggestionId)
            );
          }
        } catch {
          // Ignore parse errors from SSE
        }
      };

      es.onerror = () => {
        // EventSource will auto-reconnect; no need to crash
      };
    } catch {
      // SSE not supported or URL invalid; rely on polling
    }

    return () => {
      controller.abort();
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [conversationId, fetchSuggestions]);

  // Accept a suggestion
  const handleAccept = useCallback(
    async (suggestion: CopilotSuggestion) => {
      setActionLoading(suggestion.id);
      try {
        await apiFetch(
          `/api/operator/copilot/suggestions/${encodeURIComponent(suggestion.id)}/accept`,
          { method: 'PATCH' }
        );
        setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
        onAccept(suggestion.text);
      } catch {
        // Silently fail; the user can retry
      } finally {
        setActionLoading(null);
      }
    },
    [onAccept]
  );

  // Dismiss a suggestion
  const handleDismiss = useCallback(async (suggestionId: string) => {
    setActionLoading(suggestionId);
    try {
      await apiFetch(
        `/api/operator/copilot/suggestions/${encodeURIComponent(suggestionId)}/dismiss`,
        { method: 'PATCH' }
      );
      setSuggestions((prev) => prev.filter((s) => s.id !== suggestionId));
    } catch {
      // Silently fail
    } finally {
      setActionLoading(null);
    }
  }, []);

  // Open the "Edit & Send" dialog
  const handleOpenEdit = useCallback((suggestion: CopilotSuggestion) => {
    setEditText(suggestion.text);
    setEditingSuggestionId(suggestion.id);
    setEditDialogOpen(true);
  }, []);

  // Confirm edit and send
  const handleEditConfirm = useCallback(async () => {
    if (!editingSuggestionId || !editText.trim()) return;
    setActionLoading(editingSuggestionId);
    try {
      await apiFetch(
        `/api/operator/copilot/suggestions/${encodeURIComponent(editingSuggestionId)}/accept`,
        { method: 'PATCH' }
      );
      setSuggestions((prev) =>
        prev.filter((s) => s.id !== editingSuggestionId)
      );
      onAccept(editText.trim());
    } catch {
      // Silently fail
    } finally {
      setActionLoading(null);
      setEditDialogOpen(false);
      setEditingSuggestionId(null);
      setEditText('');
    }
  }, [editingSuggestionId, editText, onAccept]);

  // Toggle expand/collapse for a chip
  const toggleExpand = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  // Auto-collapse panel when no suggestions
  const pendingSuggestions = suggestions.filter((s) => s.status === 'PENDING');

  if (!loading && pendingSuggestions.length === 0 && !error) {
    return null;
  }

  return (
    <div className="bg-muted/20">
      {/* Panel header */}
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between px-4 py-2 text-xs font-medium text-muted-foreground hover:bg-muted/40 transition-colors"
      >
        <span className="flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-[hsl(160,84%,39%)]" />
          {t('conversations.copilot.title')}
          {pendingSuggestions.length > 0 && (
            <Badge
              variant="secondary"
              className="h-4 px-1.5 text-[10px] bg-[hsl(160,84%,39%)]/10 text-[hsl(160,60%,18%)]"
            >
              {pendingSuggestions.length}
            </Badge>
          )}
        </span>
        {collapsed ? (
          <ChevronUp className="h-3.5 w-3.5" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" />
        )}
      </button>

      {!collapsed && (
        <div className="px-4 pb-3 space-y-2">
          {/* Loading skeleton */}
          {loading && (
            <div className="flex items-center gap-2 py-2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                {t('conversations.copilot.generating')}
              </span>
            </div>
          )}

          {/* Error state */}
          {error && !loading && (
            <div className="flex items-center gap-2 py-2 text-xs text-red-500">
              <span>{error}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs px-2"
                onClick={() => fetchSuggestions()}
              >
                {t('common.refresh')}
              </Button>
            </div>
          )}

          {/* Suggestion chips */}
          {!loading &&
            pendingSuggestions.slice(0, 3).map((suggestion) => {
              const isExpanded = expandedId === suggestion.id;
              const isActionLoading = actionLoading === suggestion.id;
              const IntentIcon = INTENT_ICONS[suggestion.intent] || MessageSquare;
              const intentColor = INTENT_COLORS[suggestion.intent] || INTENT_COLORS.GENERAL_REPLY;

              return (
                <div
                  key={suggestion.id}
                  className="rounded-lg border border-border bg-background shadow-sm transition-all duration-200 hover:shadow-md"
                >
                  {/* Chip header - always visible */}
                  <button
                    type="button"
                    onClick={() => toggleExpand(suggestion.id)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-left"
                    disabled={isActionLoading}
                  >
                    <IntentIcon className="h-4 w-4 shrink-0 text-[hsl(160,60%,18%)]" />
                    <span className="flex-1 text-sm text-foreground truncate">
                      {suggestion.text}
                    </span>
                    <Badge
                      variant="outline"
                      className={`text-[10px] px-1.5 py-0 shrink-0 border ${intentColor}`}
                    >
                      {formatIntent(suggestion.intent)}
                    </Badge>
                  </button>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="px-3 pb-3 space-y-3 animate-in fade-in-0 slide-in-from-top-1 duration-150">
                      {/* Full suggestion text */}
                      <div className="rounded-md bg-muted/50 p-3 text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                        {suggestion.text}
                      </div>

                      {/* Confidence indicator */}
                      {suggestion.confidence > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                            <div
                              className="h-full rounded-full bg-[hsl(160,84%,39%)] transition-all duration-300"
                              style={{ width: `${Math.round(suggestion.confidence * 100)}%` }}
                            />
                          </div>
                          <span className="text-[10px] text-muted-foreground">
                            {Math.round(suggestion.confidence * 100)}%
                          </span>
                        </div>
                      )}

                      {/* Action buttons */}
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          className="h-7 text-xs gap-1.5 bg-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,22%)] text-white"
                          onClick={() => handleAccept(suggestion)}
                          disabled={isActionLoading}
                        >
                          {isActionLoading ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <ThumbsUp className="h-3 w-3" />
                          )}
                          {t('conversations.copilot.accept')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs gap-1.5 border-[hsl(160,60%,18%)]/20"
                          onClick={() => handleOpenEdit(suggestion)}
                          disabled={isActionLoading}
                        >
                          <Pencil className="h-3 w-3" />
                          {t('conversations.copilot.editAndSend')}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs gap-1.5 text-muted-foreground hover:text-red-600 ml-auto"
                          onClick={() => handleDismiss(suggestion.id)}
                          disabled={isActionLoading}
                        >
                          <X className="h-3 w-3" />
                          {t('conversations.copilot.dismiss')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}

      {/* Edit & Send dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-[hsl(160,84%,39%)]" />
              {t('conversations.copilot.editTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('conversations.copilot.editDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={6}
              className="resize-none border-[hsl(160,60%,18%)]/20 focus:border-[hsl(160,84%,39%)]"
              placeholder="Type your message..."
            />
            <p className="mt-1.5 text-xs text-muted-foreground">
              {editText.length} {t('conversations.copilot.characters')}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              className="border-[hsl(160,60%,18%)]/20"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleEditConfirm}
              disabled={!editText.trim() || actionLoading !== null}
              className="bg-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,22%)] text-white"
            >
              {actionLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              {t('conversations.copilot.send')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
